import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";
import { fetchBillingAccess } from "../lib/billingAccess";
import BillingNav from "../components/BillingNav";

// PI liens (billing PR #4, 2026-10-28) - SHADOW and DARK.
// Four views on one page, behind the server's billing gate like the rest of Billing
// (anyone else is sent home as if the page didn't exist):
//   /billing/pi                  the workqueue: PI cases with money owed, by law firm,
//                                plus "No attorney on file"; care complete or still in care
//   /billing/pi/firm/:payorId    one firm's cases        /billing/pi/no-attorney  those cases
//   /billing/pi/case/:caseId     one case: every charge, payment and adjustment, the checks,
//                                and the itemized statement (a PDF marked DRAFT, never sent)
// URLs carry portal row ids only (a payor id, a case id) - never a patient or case number.
// Nothing here changes Raintree; a statement only writes a billing.statement_runs row.

const PAGE = 100;

function money(v) {
  if (v === null || v === undefined || v === "") return "—";
  return Number(v).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function usDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
}

function careFrom(search) {
  return new URLSearchParams(search).get("care") === "in_care" ? "in_care" : "complete";
}

function careSearch(care) {
  return care === "in_care" ? "?care=in_care" : "";
}

function Note({ children, tone = "slate" }) {
  const cls = tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-600";
  return <div className={`mt-4 rounded-lg border p-4 text-sm ${cls}`}>{children}</div>;
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function CareToggle({ care, base }) {
  const opt = (key, label) => (
    <Link to={{ pathname: base, search: careSearch(key) }}
          className={`rounded-md px-3 py-1 text-sm ${care === key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid={`pi-care-${key}`}>
      {label}
    </Link>
  );
  return (
    <div className="inline-flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
      {opt("complete", "Care complete")}
      {opt("in_care", "Still in care")}
    </div>
  );
}

// ── the workqueue ─────────────────────────────────────────────────────

function Workqueue({ care }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    api
      .get("/billing/pi/workqueue", { params: { care } })
      .then((res) => live && setData(res.data))
      .catch(() => live && setError("Couldn't load the PI workqueue."));
    return () => {
      live = false;
    };
  }, [care]);

  const firms = useMemo(() => {
    const f = (filter || "").trim().toLowerCase();
    const list = data?.firms || [];
    return f ? list.filter((x) => (x.name || "").toLowerCase().includes(f)) : list;
  }, [data, filter]);

  if (error) return <Note tone="amber">{error}</Note>;
  if (!data) return <div className="mt-6 text-sm text-slate-500">Loading…</div>;
  const wf = data.with_firm;
  const na = data.no_attorney;
  return (
    <div data-testid="pi-workqueue">
      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Owed on PI liens" value={money(data.all.balance)} sub={`${data.all.cases.toLocaleString()} cases`} testid="pi-total" />
        <Stat label="With a law firm" value={money(wf.balance)} sub={`${wf.cases.toLocaleString()} cases`} />
        <Stat label="No attorney on file" value={money(na.balance)} sub={`${na.cases.toLocaleString()} cases`} />
        <Stat label="Never paid" value={data.all.never_paid.toLocaleString()} sub="cases with no Raintree payment" />
      </div>

      <h2 className="mt-8 text-lg font-semibold text-slate-800">
        {care === "complete" ? "Days since care ended" : "Still in care"}
      </h2>
      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm" data-testid="pi-buckets">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">{care === "complete" ? "Since care ended" : "Care end date"}</th>
              <th className="px-4 py-2 text-right">Firm cases</th>
              <th className="px-4 py-2 text-right">Firm owed</th>
              <th className="px-4 py-2 text-right">No-attorney cases</th>
              <th className="px-4 py-2 text-right">No-attorney owed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {wf.buckets.map((b, i) => (
              <tr key={b.bucket}>
                <td className="px-4 py-2 text-slate-700">{b.bucket === "no date" ? "No care end date" : `${b.bucket} days`}</td>
                <td className="px-4 py-2 text-right">{b.cases.toLocaleString()}</td>
                <td className="px-4 py-2 text-right">{money(b.balance)}</td>
                <td className="px-4 py-2 text-right">{na.buckets[i].cases.toLocaleString()}</td>
                <td className="px-4 py-2 text-right">{money(na.buckets[i].balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-800">Law firms ({data.firms.length.toLocaleString()})</h2>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter firms by name"
               className="w-64 rounded-md border border-slate-200 px-3 py-1.5 text-sm" data-testid="pi-firm-filter" />
      </div>
      {data.multi_firm_cases > 0 && (
        <p className="mt-1 text-xs text-slate-500">
          {data.multi_firm_cases} case{data.multi_firm_cases === 1 ? " has" : "s have"} two firms on file and show under
          both; the totals above count each case once.
        </p>
      )}
      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm" data-testid="pi-firms">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Law firm</th>
              <th className="px-4 py-2 text-right">Cases</th>
              <th className="px-4 py-2 text-right">Owed</th>
              <th className="px-4 py-2 text-right">Never paid</th>
              <th className="px-4 py-2 text-right">Oldest (days)</th>
              <th className="px-4 py-2">Last payment</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr className="bg-amber-50/40">
              <td className="px-4 py-2 font-medium text-amber-900">No attorney on file</td>
              <td className="px-4 py-2 text-right">{na.cases.toLocaleString()}</td>
              <td className="px-4 py-2 text-right">{money(na.balance)}</td>
              <td className="px-4 py-2 text-right">{na.never_paid.toLocaleString()}</td>
              <td className="px-4 py-2 text-right">—</td>
              <td className="px-4 py-2">—</td>
              <td className="px-4 py-2 text-right">
                <Link to={{ pathname: "/billing/pi/no-attorney", search: careSearch(care) }}
                      className="text-blue-600 hover:underline" data-testid="pi-open-no-attorney">Open</Link>
              </td>
            </tr>
            {firms.map((f) => (
              <tr key={f.payor_id} className="hover:bg-slate-50">
                <td className="px-4 py-2 font-medium text-slate-800">{f.name || `Payor #${f.payor_id}`}</td>
                <td className="px-4 py-2 text-right">{f.cases.toLocaleString()}</td>
                <td className="px-4 py-2 text-right">{money(f.balance)}</td>
                <td className="px-4 py-2 text-right">{f.never_paid.toLocaleString()}</td>
                <td className="px-4 py-2 text-right">{f.oldest_days ?? "—"}</td>
                <td className="px-4 py-2 text-slate-600">{usDate(f.last_payment)}</td>
                <td className="px-4 py-2 text-right">
                  <Link to={{ pathname: `/billing/pi/firm/${f.payor_id}`, search: careSearch(care) }}
                        className="text-blue-600 hover:underline" data-testid={`pi-open-firm-${f.payor_id}`}>Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-slate-500" data-testid="pi-as-of">
        Raintree data as of {data.as_of ? new Date(data.as_of).toLocaleString() : "—"} (Loader B, nightly). Today:{" "}
        {usDate(data.today)} (Pacific).
      </p>
    </div>
  );
}

// ── one group's cases ─────────────────────────────────────────────────

function GroupCases({ payorId, care }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    setOffset(0);              // another firm or the other care view starts on its first page
  }, [payorId, care]);

  useEffect(() => {
    let live = true;
    setError("");
    const url = payorId ? `/billing/pi/firms/${payorId}/cases` : "/billing/pi/no-attorney/cases";
    api
      .get(url, { params: { care, offset, limit: PAGE } })
      .then((res) => live && setData(res.data))
      .catch((e) => live && setError(e?.response?.status === 404 ? "No such firm." : "Couldn't load the cases."));
    return () => {
      live = false;
    };
  }, [payorId, care, offset]);

  if (error) return <Note tone="amber">{error}</Note>;
  if (!data) return <div className="mt-6 text-sm text-slate-500">Loading…</div>;
  const firm = data.firm;
  return (
    <div data-testid="pi-group">
      <h2 className="mt-6 text-xl font-semibold text-slate-900">{firm ? firm.name : "No attorney on file"}</h2>
      {firm && (
        <p className="text-sm text-slate-500">
          {[firm.address_line1, [firm.city, firm.state].filter(Boolean).join(", "), firm.zip_code, firm.phone]
            .filter(Boolean).join(" · ") || "No address on file"}
        </p>
      )}
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
        <Stat label="Cases" value={data.summary.cases.toLocaleString()} />
        <Stat label="Owed" value={money(data.summary.balance)} />
        <Stat label="Never paid" value={data.summary.never_paid.toLocaleString()} />
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm" data-testid="pi-cases">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Patient</th>
              <th className="px-4 py-2">Raintree #</th>
              <th className="px-4 py-2">Case #</th>
              <th className="px-4 py-2">Care ended</th>
              <th className="px-4 py-2 text-right">Days</th>
              <th className="px-4 py-2 text-right">Charges</th>
              <th className="px-4 py-2 text-right">Owed</th>
              <th className="px-4 py-2">Last payment</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.cases.map((c) => (
              <tr key={c.case_id} className="hover:bg-slate-50">
                <td className="px-4 py-2 font-medium text-slate-800">
                  {[c.last_name, c.first_name].filter(Boolean).join(", ")}
                  {c.other_firms.length > 0 && <span className="ml-2 text-xs text-slate-500">(+ another firm)</span>}
                </td>
                <td className="px-4 py-2 text-slate-600">{c.raintree_patient_number || "—"}</td>
                <td className="px-4 py-2 text-slate-600">{c.case_number || "—"}</td>
                <td className="px-4 py-2 text-slate-600">{usDate(c.care_end_date)}</td>
                <td className="px-4 py-2 text-right">{c.days ?? "—"}</td>
                <td className="px-4 py-2 text-right">{c.charges}</td>
                <td className="px-4 py-2 text-right">{money(c.balance)}</td>
                <td className="px-4 py-2 text-slate-600">{usDate(c.last_payment)}</td>
                <td className="px-4 py-2 text-right">
                  <Link to={`/billing/pi/case/${c.case_id}`} className="text-blue-600 hover:underline"
                        data-testid={`pi-open-case-${c.case_id}`}>Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.total > PAGE && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <button className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40" disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
          <button className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40"
                  disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>Next</button>
          <span className="text-slate-500">{offset + 1}–{Math.min(offset + PAGE, data.total)} of {data.total.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}

// ── one case ──────────────────────────────────────────────────────────

function CaseMoney({ caseId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [showVoid, setShowVoid] = useState(false);

  useEffect(() => {
    let live = true;
    setError("");
    api
      .get(`/billing/pi/cases/${caseId}`)
      .then((res) => live && setData(res.data))
      .catch((e) => live && setError(e?.response?.status === 404 ? "No such lien case." : "Couldn't load the case."));
    return () => {
      live = false;
    };
  }, [caseId]);

  const statement = () => {
    if (!window.confirm(`Generate the itemized statement for this case? It downloads a PDF marked DRAFT and records the run. Nothing is sent to anyone.`)) return;
    setBusy(true);
    setNote("");
    api
      .post(`/billing/pi/cases/${caseId}/statement`, null, { responseType: "blob" })
      .then((res) => {
        const run = res.headers?.["x-statement-run"] || "";
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `statement-case-${caseId}${run ? `-run-${run}` : ""}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        setNote(run ? `Statement run #${run} downloaded (DRAFT).` : "Statement downloaded (DRAFT).");
      })
      .catch((e) => setNote(e?.response?.status === 400 ? "Statements are for PI lien cases with charges." : "Couldn't generate the statement."))
      .finally(() => setBusy(false));
  };

  if (error) return <Note tone="amber">{error}</Note>;
  if (!data) return <div className="mt-6 text-sm text-slate-500">Loading…</div>;
  const c = data.case;
  const t = data.totals;
  const ck = data.checks;
  const isPI = c.lien_class === "PI_LIEN";
  const anyTransfers = data.charges.some((x) => Number(x.rt_transfers_net || 0) !== 0);
  return (
    <div data-testid="pi-case">
      <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            {[c.last_name, [c.first_name, c.middle_name].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
          </h2>
          <p className="text-sm text-slate-500">
            Raintree #{c.raintree_patient_number || "—"} · Case #{c.case_number || "—"} · {c.lien_class === "PI_LIEN" ? "PI lien" : c.lien_class}
            {" "}· {c.status} · Care {usDate(c.start_of_care)} to {usDate(c.care_end_date)}
            {data.days !== null && data.days !== undefined ? ` (${data.days} days ago)` : ""}
          </p>
          <p className="text-sm text-slate-500">
            Attorney: {data.attorneys.length ? data.attorneys.map((a) => a.name).join(" · ") : "No attorney on file"}
          </p>
        </div>
        {isPI && (
          <button onClick={statement} disabled={busy || data.charges.length === 0}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
                  data-testid="pi-statement">
            {busy ? "Generating…" : "Download statement (PDF, DRAFT)"}
          </button>
        )}
      </div>
      {note && <Note>{note}</Note>}
      {(ck.charges_off_their_postings > 0 || ck.charges_off_the_identity > 0 || ck.unapplied_postings > 0) && (
        <Note tone="amber">
          Checks: {ck.charges_off_their_postings} charge(s) where Raintree's amounts differ from the payment lines on file;{" "}
          {ck.charges_off_the_identity} where charge − payments − adjustments (net of reversals) − transfers ≠ balance;{" "}
          {ck.unapplied_postings} payment line(s) on the case not applied to a charge. The statement uses Raintree's
          balances.
        </Note>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Charges" value={money(t.billed)} sub={`${data.charges.length} services`} />
        <Stat label="Payments" value={money(t.payments)} />
        <Stat label="Adjustments" value={money(t.adjustments)}
              sub={Number(t.adjustment_reversals || 0) !== 0 ? `net of ${money(t.adjustment_reversals)} reversed (*)` : undefined} />
        <Stat label="Transfers" value={money(t.transfers)} />
        <Stat label="Balance" value={money(t.balance)} testid="pi-case-balance" />
      </div>

      <h3 className="mt-6 text-base font-semibold text-slate-800">Charges</h3>
      <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm" data-testid="pi-charges">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">CPT</th>
              <th className="px-3 py-2">Mod</th>
              <th className="px-3 py-2 text-right">Units</th>
              <th className="px-3 py-2 text-right">Charge</th>
              <th className="px-3 py-2 text-right">Payments</th>
              <th className="px-3 py-2 text-right">Adjustments</th>
              {anyTransfers && <th className="px-3 py-2 text-right">Transfers</th>}
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2">Last payment</th>
              <th className="px-3 py-2">Checks</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.charges.map((x) => (
              <tr key={x.charge_id}>
                <td className="px-3 py-1.5">{usDate(x.date_of_service)}</td>
                <td className="px-3 py-1.5">{x.cpt_code || "—"}</td>
                <td className="px-3 py-1.5 text-slate-600">{x.modifiers || ""}</td>
                <td className="px-3 py-1.5 text-right">{Number(x.units || 0)}</td>
                <td className="px-3 py-1.5 text-right">{money(x.billed_amount)}</td>
                <td className="px-3 py-1.5 text-right">{money(x.rt_payments_amount)}</td>
                <td className="px-3 py-1.5 text-right" title={Number(x.posted_adjustment_reversals || 0) !== 0
                  ? `Raintree adjustments ${money(x.rt_adjustments_amount)}, reversed ${money(x.posted_adjustment_reversals)}` : undefined}>
                  {money(x.adjustments_net)}{Number(x.posted_adjustment_reversals || 0) !== 0 && <span className="text-xs text-slate-500"> *</span>}
                </td>
                {anyTransfers && <td className="px-3 py-1.5 text-right">{money(x.rt_transfers_net)}</td>}
                <td className="px-3 py-1.5 text-right font-medium">{money(x.rt_current_balance)}</td>
                <td className="px-3 py-1.5 text-slate-600">{usDate(x.rt_last_payment_date)}</td>
                <td className="px-3 py-1.5 text-xs">
                  {x.recon_ok && x.identity_ok ? <span className="text-emerald-700">ok</span> : (
                    <span className="text-amber-800">
                      {!x.recon_ok && "≠ payment lines "}{!x.identity_ok && "≠ balance math"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.void_charges.length > 0 && (
        <button className="mt-2 text-sm text-slate-600 underline" onClick={() => setShowVoid(!showVoid)}>
          {showVoid ? "Hide" : "Show"} {data.void_charges.length} void charge(s) (never on the statement)
        </button>
      )}
      {showVoid && (
        <ul className="mt-2 text-sm text-slate-500">
          {data.void_charges.map((x) => (
            <li key={x.charge_id}>{usDate(x.date_of_service)} · {x.cpt_code} · {money(x.billed_amount)} (void)</li>
          ))}
        </ul>
      )}

      <h3 className="mt-6 text-base font-semibold text-slate-800">Payment lines from Raintree ({data.postings.length})</h3>
      <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm" data-testid="pi-postings">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Posted</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2">Charge</th>
              <th className="px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.postings.map((p) => (
              <tr key={p.posting_id}>
                <td className="px-3 py-1.5">{usDate(p.posted_on)}</td>
                <td className="px-3 py-1.5">{p.posting_type}</td>
                <td className="px-3 py-1.5 text-right">{money(p.amount)}</td>
                <td className="px-3 py-1.5 text-slate-600">{p.charge_id ? `#${p.charge_id}` : "not applied"}</td>
                <td className="px-3 py-1.5 text-slate-600">{p.raintree_reason_code || p.adjustment_reason || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.shadow_postings.length > 0 && (
        <Note>
          Money In (shadow) also holds {data.shadow_postings.length} proposed or approved posting(s) on this case from
          EOBs. They are not in Raintree and not on the statement.
        </Note>
      )}
    </div>
  );
}

// ── the page ──────────────────────────────────────────────────────────

export default function BillingPI() {
  const { user } = useAuth();
  const { payorId, caseId } = useParams();
  const location = useLocation();
  const [allowed, setAllowed] = useState(null);
  const care = careFrom(location.search);
  const noAttorney = location.pathname.startsWith("/billing/pi/no-attorney");

  useEffect(() => {
    let live = true;
    fetchBillingAccess(api, user?.id).then((ok) => {
      if (live) setAllowed(ok);
    });
    return () => {
      live = false;
    };
  }, [user?.id]);

  if (allowed === null) {
    return <div className="flex h-full items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!allowed) return <Navigate to="/" replace />;

  const top = !payorId && !caseId && !noAttorney;
  return (
    <div className="p-8 max-w-7xl" data-testid="billing-pi">
      <BillingNav current="pi" />
      <div className="text-sm text-slate-500">
        <Link to="/billing" className="text-sky-700 hover:underline">Billing</Link>
        {" / "}
        {top ? "PI liens" : <Link to={{ pathname: "/billing/pi", search: careSearch(care) }} className="text-sky-700 hover:underline">PI liens</Link>}
      </div>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-slate-900">PI liens</h1>
        {!caseId && <CareToggle care={care} base={location.pathname} />}
      </div>
      <p className="mt-1 text-sm text-slate-500" data-testid="billing-pi-shadow-note">
        Shadow mode. Read from the Raintree mirror; nothing here changes Raintree, and statements are DRAFT downloads
        that go to no one.
      </p>
      {caseId ? <CaseMoney caseId={caseId} /> : (payorId || noAttorney) ? (
        <GroupCases payorId={payorId} care={care} />
      ) : (
        <Workqueue care={care} />
      )}
    </div>
  );
}
